import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Printer, ArrowLeft } from 'lucide-react';
import { getRecibo, BolRecibo, money, fmtEventDate } from '../api';
import logo from '../../../assets/logo-badge.webp';

// Recibo de una compra de boletos del festival (Mabel 28-oct-2026: «el pago tiene que generar un recibo; le tomo foto o se lo mando
// en PDF… al final llegan con su recibo y les doy su lugar»). Mismo molde que /recibo/:id (ReceiptPage): logo, estudio, folio, total,
// «recibido por». Imprimir = window.print(); el CSS global @media print solo deja .print-area (cabe en una hoja carta).
function fmtFH(s: string | null | undefined) {
  const m = s?.match(/^(\d{4})-(\d{2})-(\d{2})[T ]?(\d{2})?:?(\d{2})?/);
  if (!m) return '—';
  const d = new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' });
  return m[4] ? `${d} · ${m[4]}:${m[5]}` : d;
}

export default function EventTicketReceiptPage() {
  const { id, tid } = useParams();
  const navigate = useNavigate();
  const [r, setR] = useState<BolRecibo | null>(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    getRecibo(Number(id), Number(tid)).then(setR).catch((e: any) => setErr(e?.message || 'No se encontró la venta.'));
  }, [id, tid]);

  if (err) return <div className="mx-auto max-w-md p-6 text-center text-content-3">{err}</div>;
  if (!r) return <div className="p-6 text-content-3">Cargando recibo…</div>;

  const k = r.data, ev = r.evento;
  const folio = k.folio || `${r.studio.prefix}-${String(k.id).padStart(5, '0')}`;
  // Karla 28-oct: solo lo que QUEDA en lista de espera (no el total pedido); el backend lo calcula (en adicionales: lo que le resta).
  const espera = k.espera ?? Math.max(0, k.qty_requested - k.qty_sold);
  const base = k.folio_base || folio;
  const sorteo = k.participa_sorteo !== false && !k.cortesia;
  const familia = (k.familia || []).filter((n) => n !== k.comprador);

  return (
    <div className="mx-auto max-w-lg" data-recibo-boletos>
      {/* Controles (no se imprimen) */}
      <div className="no-print mb-4 flex flex-wrap items-center justify-between gap-2">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-content-2 hover:text-content"><ArrowLeft size={15} /> Volver</button>
        <div className="flex gap-2">
          <Link to={`/eventos/${ev.event_id}`} className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-2 text-sm font-medium text-content-2 hover:bg-surface-2">Boletaje</Link>
          <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 rounded-lg bg-accent px-3 py-2 text-sm font-semibold text-on-accent transition hover:opacity-90" data-imprimir>
            <Printer size={16} /> Imprimir / Guardar PDF
          </button>
        </div>
      </div>
      {k.cancelled && <div className="no-print mb-3 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm font-semibold text-red-600">Esta venta está ANULADA: el recibo ya no vale.</div>}

      {/* Recibo imprimible */}
      <div className="print-area rounded-2xl border border-line bg-surface p-5 shadow-sm sm:p-8">
        <div className="flex items-center gap-3 border-b border-line pb-4 sm:gap-4 sm:pb-5">
          <img src={logo} alt="" className="h-14 w-14 rounded-full bg-white object-contain ring-1 ring-line sm:h-16 sm:w-16" />
          <div className="min-w-0 flex-1">
            <div className="font-display text-sm font-bold leading-tight text-content sm:text-lg">{r.studio.name}</div>
            <div className="text-[11px] leading-snug text-content-3 sm:text-xs">{r.studio.address}</div>
          </div>
          <div className="shrink-0 text-right">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-content-3 sm:text-xs">{k.cortesia ? 'Cortesía' : 'Recibo de boletos'}</div>
            <div className="font-display text-sm font-bold text-content" data-folio>{folio}</div>
          </div>
        </div>

        <div className="py-4 sm:py-5">
          <div className="text-xs font-medium uppercase tracking-wide text-content-3">Evento</div>
          <div className="text-base font-semibold text-content">{ev.name}</div>
          <div className="text-sm capitalize text-content-2">{fmtEventDate(ev.datetime)}</div>
          {(ev.venue || ev.venue_address) && <div className="text-sm text-content-2">{[ev.venue, ev.venue_address].filter(Boolean).join(' — ')}</div>}

          {/* Número de participación: con él entra al sorteo para elegir asientos (uno por familia; -B, -C = compras siguientes) */}
          {sorteo ? (
            <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border-2 border-accent/60 bg-accent/10 px-4 py-3" data-participacion>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wide text-content-3 sm:text-xs">Número de participación para el sorteo</div>
                <div className="text-[11px] text-content-2">{k.sufijo ? `Compra ${k.sufijo} del mismo número: entra UNA vez al sorteo.` : familia.length ? 'Un número por familia: entra UNA vez al sorteo.' : 'Con este número participas en el sorteo para elegir asientos.'}</div>
                {familia.length > 0 && <div className="text-[11px] text-content-2">Familia{k.family_label ? ` «${k.family_label}»` : ''}: {familia.join(', ')}</div>}
              </div>
              <div className="shrink-0 whitespace-nowrap font-display text-xl font-bold tracking-wide text-content sm:text-3xl" data-numero-participacion>{base}</div>
            </div>
          ) : (
            <div className="mt-4 rounded-xl border border-line bg-surface-2/60 px-4 py-2 text-xs text-content-2" data-sin-sorteo>{k.cortesia ? 'Boleto de cortesía para maestros y staff.' : 'Maestros y staff'}: no entra al sorteo de asientos.</div>
          )}

          <div className="mt-4 grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-content-3">Recibí de</div>
              <div className="text-base font-semibold text-content" data-comprador>{k.comprador || '—'}</div>
              {k.telefono && <div className="text-xs text-content-3">{k.telefono}</div>}
            </div>
            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-content-3">Fecha de pago</div>
              <div className="text-sm text-content">{fmtFH(k.paid_at)}</div>
            </div>
          </div>

          <div className="mt-4 overflow-hidden rounded-xl border border-line">
            <table className="w-full text-sm">
              <thead><tr className="bg-surface-2/60 text-left text-[10px] uppercase tracking-wide text-content-3 sm:text-xs"><th className="px-2 py-2 sm:px-3">Concepto</th><th className="px-2 py-2 text-right sm:px-3">Boletos</th><th className="px-2 py-2 text-right sm:px-3">Precio</th><th className="px-2 py-2 text-right sm:px-3">Importe</th></tr></thead>
              <tbody>
                <tr className="border-t border-line">
                  <td className="px-2 py-2 text-content sm:px-3">{k.cortesia ? 'Boleto de cortesía' : `Boleto${k.qty_sold === 1 ? '' : 's'} del festival`}{k.phase_name ? <span className="block text-xs text-content-3">{k.phase_name}</span> : null}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-content sm:px-3" data-boletos>{k.qty_sold}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-content sm:px-3">{k.unit_price != null ? money(k.unit_price) : '—'}</td>
                  <td className="px-2 py-2 text-right tabular-nums font-semibold text-content sm:px-3">{money(k.amount)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-content-3">Forma de pago</div>
              <div className="text-sm text-content">{k.metodo || '—'}</div>
            </div>
            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-content-3">Lista de espera</div>
              <div className="text-sm text-content" data-espera>{espera > 0 ? `${espera} boleto${espera === 1 ? '' : 's'} pendiente${espera === 1 ? '' : 's'}` : 'Sin boletos en espera'}</div>
            </div>
          </div>
          {k.note && (
            <div className="mt-4">
              <div className="text-xs font-medium uppercase tracking-wide text-content-3">Observaciones</div>
              <div className="text-sm text-content-2">{k.note}</div>
            </div>
          )}
        </div>

        {/* Total */}
        <div className="flex items-center justify-between rounded-xl bg-accent/10 px-5 py-4">
          <span className="font-display text-sm font-semibold uppercase tracking-wide text-content-2">Total pagado</span>
          <span className="font-display text-2xl font-bold text-content" data-total>{money(k.amount)}<span className="ml-1 text-sm font-medium text-content-3">MXN</span></span>
        </div>

        <div className="mt-6 flex items-end justify-between gap-4 sm:mt-8">
          <div className="text-xs text-content-3">
            <div className="mb-6">Atendió</div>
            <div className="border-t border-line pt-1">{k.attended_name || '—'}</div>
          </div>
          <div className="max-w-[55%] text-right text-[10px] text-content-3">
            <strong className="block text-[11px] text-content">{sorteo ? 'Presenta este recibo el día del sorteo para elegir tus asientos.' : 'Presenta este recibo para recoger tus boletos.'}</strong>
            Conserva este recibo como comprobante. ¡Gracias por ser parte de Let's Move! 💛
          </div>
        </div>
      </div>
    </div>
  );
}
